import React, { memo } from 'react';
import { Table, Tooltip } from 'antd';
import {
  FormattedMessage,
  FormattedNumber,
  defineMessage,
  MessageDescriptor,
} from 'react-intl';
import { TablePaginationConfig } from 'antd/es/table/interface';
import ProductImg from 'assets/images/icons/product.svg';
import ServiceImg from 'assets/images/icons/service.svg';
import GiftcardImg from 'assets/images/icons/giftcard.svg';
import {
  SalesReportRecord,
  SalesReportTableParams,
} from 'utils/apiClient/services/reports/types';

export interface Props {
  loading: boolean;
  currency: string;
  data: Array<SalesReportRecord> | undefined;
  handleTableChange: (t: SalesReportTableParams) => void;
  tableParams: SalesReportTableParams;
}

const typeMap: Record<string, [MessageDescriptor, React.ReactNode]> = {
  product: [
    defineMessage({ defaultMessage: 'Product' }),
    <ProductImg key="p" css="color: #54b25c; margin-top: 5px;" />,
  ],
  service: [
    defineMessage({ defaultMessage: 'Service' }),
    <ServiceImg key="s" css="color: #0099ff; margin-top: 5px;" />,
  ],
  gift_card: [
    defineMessage({ defaultMessage: 'Giftcard' }),
    <GiftcardImg key="g" css="color: #5a5ed3; margin-top: 5px;" />,
  ],
};

const SalesReportTable: React.FC<Props> = ({
  loading,
  currency,
  data,
  handleTableChange,
  tableParams,
}) => (
  <Table<SalesReportRecord>
    css={`
      .ant-table-thead > tr {
        height: 45px;
      }
      .ant-table-placeholder {
        min-height: calc(100vh - 300px);
      }
    `}
    loading={loading}
    dataSource={data}
    rowKey={({ id }) => id}
    size="small"
    pagination={tableParams.pagination as TablePaginationConfig}
    onChange={(pagination, filters, sorter) =>
      handleTableChange({ pagination, filters, sorter })
    }
    scroll={{ x: true }}
  >
    <Table.Column<SalesReportRecord>
      key="position"
      width="5%"
      title={<FormattedMessage defaultMessage="Nr." />}
      dataIndex="position"
    />
    <Table.Column<SalesReportRecord>
      ellipsis
      key="name"
      width="30%"
      sorter
      title={<FormattedMessage defaultMessage="Name" />}
      render={(r: SalesReportRecord) => (
        <span css="color: #54b25c;">{r.name}</span>
      )}
    />
    <Table.Column<SalesReportRecord>
      key="type"
      width="10%"
      title={<FormattedMessage defaultMessage="Type" />}
      render={(r: SalesReportRecord) => {
        if (!r.type || !(r.type in typeMap)) {
          return null;
        }
        const d = typeMap[r.type];
        return (
          <Tooltip title={<FormattedMessage {...d[0]} />} placement="right">
            {d[1]}
          </Tooltip>
        );
      }}
      filters={[
        {
          value: 'product',
          text: <FormattedMessage defaultMessage="Product" />,
        },
        {
          value: 'service',
          text: <FormattedMessage defaultMessage="Service" />,
        },
        {
          value: 'gift_card',
          text: <FormattedMessage defaultMessage="Giftcard" />,
        },
      ]}
    />
    <Table.Column<SalesReportRecord>
      key="gross_price"
      width="10%"
      title={<FormattedMessage defaultMessage="Selling price" />}
      render={(r: SalesReportRecord) => (
        <FormattedNumber
          style="currency" // eslint-disable-line react/style-prop-object
          currency={currency}
          value={r.gross_price}
        />
      )}
    />
    <Table.Column<SalesReportRecord>
      key="sold"
      width="10%"
      sorter
      title={<FormattedMessage defaultMessage="Sold" />}
      dataIndex="sold"
    />
    <Table.Column<SalesReportRecord>
      key="returned"
      width="10%"
      title={<FormattedMessage defaultMessage="Returned" />}
      dataIndex="returned"
    />
    <Table.Column<SalesReportRecord>
      key="total"
      width="10%"
      title={<FormattedMessage defaultMessage="Total" />}
      dataIndex="total"
    />
    <Table.Column<SalesReportRecord>
      key="revenue_gross"
      width="15%"
      sorter
      defaultSortOrder="descend"
      title={<FormattedMessage defaultMessage="Revenue overall" />}
      render={(r: SalesReportRecord) => (
        <FormattedNumber
          style="currency" // eslint-disable-line react/style-prop-object
          currency={currency}
          value={r.revenue_gross}
        />
      )}
    />
  </Table>
);

export default memo(SalesReportTable);
